import React, { useState, useEffect } from 'react';
import {
  FiShield, FiShoppingBag, FiRefreshCw, FiCheck, FiCopy, FiCheckCircle, FiCreditCard, FiSmartphone
} from 'react-icons/fi';
import { smsAPI } from '../../services/api';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';

const SmsPacksPage = () => {
  const [wallet, setWallet] = useState(null);
  const [packs, setPacks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [logs, setLogs] = useState([]);
  const [platformPaymentMethods, setPlatformPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('packs'); // 'packs' | 'orders' | 'logs'
  const [feedback, setFeedback] = useState(null);
  const [copiedText, setCopiedText] = useState(false);

  // Modal Achat de Pack SMS
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedMethodId, setSelectedMethodId] = useState('');
  const [orderForm, setOrderForm] = useState({
    paymentMethod: 'orange_money',
    paymentPhone: '',
    paymentReference: '',
  });
  const [submittingOrder, setSubmittingOrder] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [walletRes, packsRes, ordersRes, logsRes, pmRes] = await Promise.all([
        smsAPI.getWallet(),
        smsAPI.getPacks(),
        smsAPI.getOrders(),
        smsAPI.getLogs({ limit: 50 }),
        smsAPI.getPlatformPaymentMethods().catch(() => ({ data: [] })),
      ]);
      setWallet(walletRes.data);
      setPacks(packsRes.data || []);
      setOrders(ordersRes.data || []);
      setLogs(logsRes.data?.logs || []);
      setPlatformPaymentMethods(pmRes.data || []);
    } catch (error) {
      console.error('Erreur chargement données SMS:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenOrderModal = (pack) => {
    setSelectedPack(pack);
    const defaultMethod = platformPaymentMethods[0] || null;
    setSelectedMethodId(defaultMethod?.id || '');
    setOrderForm({
      paymentMethod: defaultMethod?.provider || 'orange_money',
      paymentPhone: '',
      paymentReference: '',
    });
    setCopiedText(false);
  };

  const handleSelectMethod = (pm) => {
    setSelectedMethodId(pm.id);
    setOrderForm(prev => ({
      ...prev,
      paymentMethod: pm.provider,
    }));
    setCopiedText(false);
  };

  const handleCopyNumber = (number) => {
    navigator.clipboard.writeText(number);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2500);
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!selectedPack) return;

    if (!orderForm.paymentReference.trim()) {
      alert('Veuillez renseigner la référence de transaction reçue par SMS lors de votre paiement.');
      return;
    }

    setSubmittingOrder(true);
    setFeedback(null);
    try {
      const res = await smsAPI.createOrder({
        packId: selectedPack.id,
        paymentMethod: orderForm.paymentMethod,
        paymentPhone: orderForm.paymentPhone,
        paymentReference: orderForm.paymentReference,
      });

      setFeedback({
        type: 'success',
        text: res.data?.message || 'Commande soumise avec succès ! Les SMS seront crédités dès validation par le Super Administrateur.'
      });
      setSelectedPack(null);
      setTab('orders');
      fetchData();
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de la soumission de la commande.'
      });
    } finally {
      setSubmittingOrder(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  // Méthode de paiement active dans le modal
  const activePaymentMethod = platformPaymentMethods.find(m => m.id === selectedMethodId) || platformPaymentMethods[0] || {
    provider: 'orange_money',
    name: 'Orange Money Mali',
    phone_number: '+223 78 86 64 73',
    account_holder_name: 'MaliImmo Plateforme SaaS',
    merchant_code: 'CI-788664',
    instructions: 'Composer le #144#2#2# ou via l\'application Orange Money vers le numéro marchand ci-dessus.',
  };

  const orderColumns = [
    {
      header: 'Pack / SMS',
      render: r => (
        <div>
          <span className="badge badge-info" style={{ fontWeight: 700, fontSize: 12 }}>
            🪙 {r.sms_count} SMS
          </span>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            {r.pack_name || 'Pack standard'}
          </div>
        </div>
      )
    },
    {
      header: 'Montant Réglé',
      render: r => <strong>{fmt(r.price)} XOF</strong>
    },
    {
      header: 'Mode & Compte Réception',
      render: r => (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 700, textTransform: 'capitalize', color: r.payment_method?.includes('orange') ? '#ea580c' : (r.payment_method?.includes('moov') ? '#2563eb' : '#059669') }}>
            📱 {r.payment_method?.replace('_', ' ').toUpperCase()}
          </div>
          {r.payment_phone && <div>Payeur : <strong>{r.payment_phone}</strong></div>}
          <div>Réf : <code>{r.payment_reference}</code></div>
        </div>
      )
    },
    {
      header: 'Statut de Validation',
      render: r => {
        let badge = 'warning';
        let label = '⏳ En attente validation Super Admin';
        if (r.status === 'approved') {
          badge = 'success';
          label = '✅ Validée & SMS Crédités';
        } else if (r.status === 'rejected') {
          badge = 'danger';
          label = '❌ Rejetée';
        }

        return (
          <div>
            <span className={`badge badge-${badge}`} style={{ fontWeight: 700, fontSize: 11 }}>
              {label}
            </span>
            {r.rejection_reason && (
              <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>
                Motif : {r.rejection_reason}
              </div>
            )}
          </div>
        );
      }
    },
    {
      header: 'Date de Demande',
      render: r => <span style={{ fontSize: 12 }}>{fmtDate(r.created_at)}</span>
    }
  ];

  const logColumns = [
    {
      header: 'Destinataire',
      render: r => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.recipient_name || 'Locataire'}</div>
          <div style={{ fontSize: 11, color: '#2563eb' }}>📞 {r.recipient_phone}</div>
        </div>
      )
    },
    {
      header: 'Message Envoyé',
      render: r => (
        <div style={{ maxWidth: 360, fontSize: 12, lineHeight: 1.4 }}>
          {r.message}
        </div>
      )
    },
    {
      header: 'Solde Agence',
      render: r => (
        <div style={{ fontSize: 12 }}>
          {r.agency_credits_before !== null ? `${r.agency_credits_before} → ${r.agency_credits_after} SMS` : '—'}
        </div>
      )
    },
    {
      header: 'Statut',
      render: r => (
        <span className={`badge badge-${r.status === 'sent' ? 'success' : (r.status === 'simulated' ? 'info' : 'danger')}`} style={{ fontSize: 11 }}>
          {r.status === 'sent' ? '✅ Délivré' : (r.status === 'simulated' ? '📱 Simulé' : '❌ Échec')}
        </span>
      )
    },
    {
      header: 'Date',
      render: r => <span style={{ fontSize: 12 }}>{fmtDate(r.created_at)}</span>
    }
  ];

  return (
    <div className="page-container" style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Portefeuille & Recharge SMS (BatakiPlus)</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Rechargez vos crédits SMS via Orange Money ou Moov Money pour envoyer vos rappels de loyers et quittances.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={fetchData}>
            <FiRefreshCw style={{ marginRight: 4 }} /> Actualiser
          </button>
        </div>
      </div>

      {/* Feedback message */}
      {feedback && (
        <div className="card" style={{
          marginBottom: 20,
          background: feedback.type === 'success' ? '#dcfce7' : '#fee2e2',
          borderLeft: `5px solid ${feedback.type === 'success' ? '#22c55e' : '#ef4444'}`,
          padding: 14
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontWeight: 600, color: feedback.type === 'success' ? '#15803d' : '#b91c1c', fontSize: 13 }}>
              {feedback.text}
            </span>
            <button className="btn btn-sm btn-secondary" onClick={() => setFeedback(null)}>OK</button>
          </div>
        </div>
      )}

      {/* Solde Wallet Widget */}
      <div className="card" style={{
        marginBottom: 24,
        background: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)',
        color: '#ffffff',
        border: 'none',
        boxShadow: '0 6px 20px rgba(124, 58, 237, 0.2)'
      }}>
        <div className="card-body" style={{ padding: '18px 22px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Portefeuille SMS Agence
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, margin: '4px 0 2px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                🪙 {fmt(wallet?.sms_credits || 0)} <span style={{ fontSize: 15, fontWeight: 500, opacity: 0.9 }}>crédits disponibles</span>
              </div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                Total envoyés par votre agence : <strong>{fmt(wallet?.total_sms_sent || 0)} SMS</strong>
              </div>
            </div>
            <div>
              <button
                className="btn btn-sm"
                style={{
                  background: '#ffffff',
                  color: '#6d28d9',
                  fontWeight: 700,
                  fontSize: 13,
                  padding: '8px 16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
                onClick={() => {
                  setTab('packs');
                  const pack = packs[1] || packs[0];
                  if (pack) handleOpenOrderModal(pack);
                }}
              >
                ⚡ Recharger mes SMS
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Onglets */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--border-color)', paddingBottom: 10, flexWrap: 'wrap' }}>
        <button
          className={`btn btn-sm ${tab === 'packs' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('packs')}
        >
          🎁 Grille des Packs SMS ({packs.length})
        </button>
        <button
          className={`btn btn-sm ${tab === 'orders' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('orders')}
        >
          📋 Mes Commandes ({orders.length})
        </button>
        <button
          className={`btn btn-sm ${tab === 'logs' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setTab('logs')}
        >
          📜 Historique des envois ({logs.length})
        </button>
      </div>

      {/* ── ONGLET 1: GRILLE DES PACKS SMS ── */}
      {tab === 'packs' && (
        <div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 14
          }}>
            {packs.map((pack) => (
              <div
                key={pack.id}
                className="card"
                style={{
                  padding: '14px',
                  borderRadius: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: pack.is_popular ? '2px solid #7c3aed' : '1px solid var(--border-color)',
                  position: 'relative',
                  background: pack.is_popular ? 'linear-gradient(180deg, #faf5ff 0%, #ffffff 100%)' : '#ffffff',
                  boxShadow: pack.is_popular ? '0 4px 14px rgba(124, 58, 237, 0.1)' : 'none'
                }}
              >
                {pack.badge && (
                  <span style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: pack.is_popular ? '#7c3aed' : '#2563eb',
                    color: '#ffffff',
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 12
                  }}>
                    {pack.badge}
                  </span>
                )}

                <div>
                  <h3 style={{ margin: '0 0 2px 0', fontSize: 14, fontWeight: 700 }}>{pack.name}</h3>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#7c3aed', margin: '4px 0' }}>
                    {fmt(pack.sms_count)} <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>SMS</span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                    {fmt(pack.price)} XOF
                  </div>
                  <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, marginTop: 2 }}>
                    ≈ {parseFloat(pack.unit_price || (pack.price / pack.sms_count)).toFixed(1)} F / SMS
                  </div>
                  {pack.description && (
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '6px 0 0 0', lineHeight: 1.3 }}>
                      {pack.description}
                    </p>
                  )}
                </div>

                <button
                  className="btn btn-sm btn-primary"
                  style={{
                    width: '100%',
                    marginTop: 12,
                    background: pack.is_popular ? '#7c3aed' : '#2563eb',
                    borderColor: pack.is_popular ? '#7c3aed' : '#2563eb',
                    fontWeight: 600,
                    fontSize: 12,
                    padding: '7px 10px'
                  }}
                  onClick={() => handleOpenOrderModal(pack)}
                >
                  <FiShoppingBag style={{ marginRight: 4 }} size={13} /> Commander ({fmt(pack.price)} F)
                </button>
              </div>
            ))}
          </div>

          {/* Guide explicatif du fonctionnement */}
          <div className="card" style={{ marginTop: 20, background: '#f8fafc' }}>
            <div className="card-body" style={{ padding: 16 }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiShield style={{ color: '#7c3aed' }} /> Comment fonctionne la recharge de vos crédits SMS ?
              </h4>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
                1. <strong>Choisissez votre pack</strong> selon vos besoins mensuels.<br />
                2. Réglez via <strong>Orange Money</strong> ou <strong>Moov Money</strong> sur le compte marchand Super Admin affiché dans le formulaire.<br />
                3. Renseignez votre <strong>référence de transaction</strong> reçue par SMS.<br />
                4. Dès validation par le Super Admin, vos crédits sont alloués automatiquement à votre agence.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── ONGLET 2: MES COMMANDES DE SMS ── */}
      {tab === 'orders' && (
        <div className="card">
          <div className="card-header">
            <h2 style={{ fontSize: 16 }}>Historique de mes commandes de packs SMS ({orders.length})</h2>
          </div>
          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : (
            <DataTable columns={orderColumns} data={orders} emptyMessage="Vous n'avez pas encore passé de commande de SMS." />
          )}
        </div>
      )}

      {/* ── ONGLET 3: HISTORIQUE DES SMS ENVOYÉS ── */}
      {tab === 'logs' && (
        <div className="card">
          <div className="card-header">
            <h2 style={{ fontSize: 16 }}>Journal des SMS envoyés par votre agence ({logs.length})</h2>
          </div>
          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : (
            <DataTable columns={logColumns} data={logs} emptyMessage="Aucun SMS envoyé pour le moment." />
          )}
        </div>
      )}

      {/* MODAL ACHAT PACK SMS AVEC COMPTES MARCHANDS SUPER ADMIN */}
      {selectedPack && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedPack(null)}
          title={`📱 Commander : ${selectedPack.name} (${fmt(selectedPack.sms_count)} SMS)`}
        >
          <form onSubmit={handleSubmitOrder}>
            {/* Récapitulatif pack */}
            <div style={{ background: '#faf5ff', border: '1px solid #ddd6fe', padding: '12px 16px', borderRadius: 10, marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{ color: '#4c1d95', fontSize: 15 }}>{selectedPack.name}</strong>
                <div style={{ fontSize: 12, color: '#6d28d9' }}>🪙 {fmt(selectedPack.sms_count)} SMS de notification</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#7c3aed' }}>
                  {fmt(selectedPack.price)} XOF
                </div>
                <div style={{ fontSize: 10, color: '#6d28d9' }}>TTC</div>
              </div>
            </div>

            {/* Choix du canal de paiement Super Admin (Orange Money / Moov Money / Wave) */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 700, fontSize: 12, color: '#334155', marginBottom: 6, display: 'block' }}>
                Sélectionnez le canal de paiement du Super Admin :
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 6 }}>
                {platformPaymentMethods.map((pm) => (
                  <button
                    key={pm.id}
                    type="button"
                    onClick={() => handleSelectMethod(pm)}
                    className={`btn btn-sm ${selectedMethodId === pm.id ? 'btn-primary' : 'btn-outline'}`}
                    style={{
                      padding: '8px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6
                    }}
                  >
                    <FiSmartphone size={13} />
                    {pm.name.replace('Mali', '').trim()}
                  </button>
                ))}
              </div>
            </div>

            {/* Instructions & Compte Marchand Super Admin */}
            <div style={{
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
              borderRadius: 10,
              padding: '12px 14px',
              marginBottom: 14
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiCreditCard size={15} color="#2563eb" />
                  Compte Récepteur Super Admin :
                </div>
                {activePaymentMethod.account_type === 'merchant' && (
                  <span style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                    Compte Marchand
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Numéro / Code Marchand :</div>
                  <strong style={{ fontSize: 16, color: '#0f172a', letterSpacing: 0.5 }}>
                    {activePaymentMethod.phone_number} {activePaymentMethod.merchant_code ? `(${activePaymentMethod.merchant_code})` : ''}
                  </strong>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopyNumber(activePaymentMethod.phone_number)}
                  className="btn btn-sm btn-outline"
                  style={{ padding: '4px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {copiedText ? <><FiCheck color="#16a34a" /> Copié !</> : <><FiCopy /> Copier</>}
                </button>
              </div>

              <div style={{ fontSize: 12, color: '#475569' }}>
                Titulaire : <strong>{activePaymentMethod.account_holder_name}</strong>
              </div>
              {activePaymentMethod.instructions && (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontStyle: 'italic' }}>
                  ℹ️ {activePaymentMethod.instructions}
                </div>
              )}
            </div>

            {/* Saisie numéro payeur */}
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, display: 'block' }}>
                Votre numéro de téléphone émetteur (payeur) *
              </label>
              <input
                type="tel"
                className="form-control"
                placeholder="Ex: +223 70 12 34 56"
                required
                value={orderForm.paymentPhone}
                onChange={(e) => setOrderForm({ ...orderForm, paymentPhone: e.target.value })}
              />
            </div>

            {/* Saisie référence */}
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, display: 'block' }}>
                Référence de transaction reçue par SMS *
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: CI240904.1234.A56789 ou MP2409..."
                required
                value={orderForm.paymentReference}
                onChange={(e) => setOrderForm({ ...orderForm, paymentReference: e.target.value })}
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 2, display: 'block' }}>
                Le Super Admin vérifiera cette référence sur son compte marchand avant de créditer vos SMS.
              </small>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setSelectedPack(null)}>
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submittingOrder}
                style={{ background: '#7c3aed', borderColor: '#7c3aed', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <FiCheckCircle size={15} />
                {submittingOrder ? 'Soumission...' : 'Confirmer le règlement'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default SmsPacksPage;
